import React, { useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SignedOut, SignedIn, useUser } from "@clerk/clerk-react";
import { Spinner } from "@material-tailwind/react";
import Navbar from "./components/header/Navbar";
import Footer from "./components/footer/Footer";

/* ── Eager: critical path only (first paint) ── */
import Home from "./pages/Home";
import DestinationSelector from "./pages/DestinationSelector";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import CountryNavbar from "./components/header/CountryNavbar";

/* ── Lazy: heavy / secondary routes – loaded on demand ── */
const AboutUs = lazy(() => import("./pages/AboutUs"));
const RedirectUser = lazy(() => import("./pages/RedirectUser"));
const ApplyNow = lazy(() => import("./pages/ApplyNow"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./components/JobDetail"));
// const AdminRoute = lazy(() => import("./components/AdminRoute"));
// const PayrollTemplates = lazy(() => import("./pages/PayrollTemplates"));
const TermsAndConditions = lazy(
  () => import("./pages/legal/TermsAndConditions"),
);
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const InformationSecurityPolicy = lazy(
  () => import("./pages/legal/InformationSecurityPolicy"),
);

/* Shared loading fallback for lazy routes */
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-12 h-12 border-4 border-transparent border-t-myBlue rounded-full animate-spin" />
  </div>
);

const App = () => {
  const location = useLocation();
  const { isSignedIn, isLoaded } = useUser();

  const [cookiesPop, setCookiesPop] = useState(true);
  const [loading, setLoading] = useState(false);
  const [decline, setDecline] = useState(false);

  const [hasDestination, setHasDestination] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState("");

  // ------------------------------------------------------------
  // Safe localStorage helpers
  // ------------------------------------------------------------

  const getLocalStorageItem = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`error accessing localStorage "${key}":`, error);
      return null;
    }
  };

  const setLocalStorageItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Error setting localStorage "${key}":`, error);
    }
  };

  // ------------------------------------------------------------
  // Sync destination state with localStorage
  // ------------------------------------------------------------

  useEffect(() => {
    const syncDestination = () => {
      const destination = getLocalStorageItem("destination");

      setSelectedDestination(destination || "");
      setHasDestination(Boolean(destination));
    };

    // Initial check
    syncDestination();

    // DestinationSelector / other components can notify App
    window.addEventListener("storageUpdate", syncDestination);

    // Native storage event (useful if another tab changes it)
    window.addEventListener("storage", syncDestination);

    return () => {
      window.removeEventListener("storageUpdate", syncDestination);
      window.removeEventListener("storage", syncDestination);
    };
  }, []);

  // ------------------------------------------------------------
  // IMPORTANT:
  // Clear destination when user signs out.
  //
  // Also update React state immediately.
  // ------------------------------------------------------------

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      try {
        localStorage.removeItem("destination");
      } catch (error) {
        console.error("Error removing destination:", error);
      }

      setSelectedDestination("");
      setHasDestination(false);
    }
  }, [isSignedIn, isLoaded]);

  // ------------------------------------------------------------
  // Cookies
  // ------------------------------------------------------------

  useEffect(() => {
    const cookiesAccepted = getLocalStorageItem("cookiesAccepted");

    if (cookiesAccepted === "true") {
      setCookiesPop(false);
    }
  }, []);

  const handleAccept = () => {
    setLoading(true);

    setTimeout(() => {
      setLoading(false);

      setLocalStorageItem("cookiesAccepted", "true");
      setCookiesPop(false);
    }, 2000);
  };

  const handleDecline = () => {
    setDecline(true);

    setTimeout(() => {
      setDecline(false);

      setLocalStorageItem("cookiesAccepted", "false");
      setCookiesPop(false);
    }, 2000);
  };

  // ------------------------------------------------------------
  // Public layout
  // ------------------------------------------------------------

  const PublicLayout = ({ children, showCountryNavbar = false }) => (
    <>
      {showCountryNavbar && selectedDestination && (
        <CountryNavbar selectedCountry={selectedDestination} />
      )}

      <Navbar />

      {children}

      <Footer />
    </>
  );

  // ------------------------------------------------------------
  // Protected layout
  // ------------------------------------------------------------

  const ProtectedLayout = ({ children, showCountryNavbar = true }) => (
    <>
      {showCountryNavbar && selectedDestination && (
        <CountryNavbar selectedCountry={selectedDestination} />
      )}

      <Navbar />

      {children}

      <Footer />
    </>
  );

  // ------------------------------------------------------------
  // Wait for Clerk to determine authentication state
  // ------------------------------------------------------------

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen blur-3xl">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {/* =========================================================
          AUTH PAGES
          ========================================================= */}

      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
      </Routes>

      {/* =========================================================
          SIGNED OUT
          ========================================================= */}

      <SignedOut>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* -----------------------------------------------------
                PUBLIC HOME
                ----------------------------------------------------- */}

            <Route
              path="/"
              element={
                <PublicLayout>
                  <Home />
                </PublicLayout>
              }
            />

            <Route
              path="/home"
              element={
                <PublicLayout>
                  <Home />
                </PublicLayout>
              }
            />

            {/* -----------------------------------------------------
                PUBLIC ABOUT
                ----------------------------------------------------- */}

            <Route
              path="/about-us"
              element={
                <PublicLayout>
                  <AboutUs />
                </PublicLayout>
              }
            />

            {/* -----------------------------------------------------
                APPLY NOW
                Signed-out users go through your RedirectUser
                authentication flow.
                ----------------------------------------------------- */}

            <Route
              path="/apply-now"
              element={
                <PublicLayout>
                  <RedirectUser />
                </PublicLayout>
              }
            />

            {/* -----------------------------------------------------
                JOBS
                ----------------------------------------------------- */}

            <Route
              path="/jobs"
              element={
                <PublicLayout>
                  <RedirectUser />
                </PublicLayout>
              }
            />

            <Route
              path="/jobs/:page/:id"
              element={
                <PublicLayout>
                  <JobDetail />
                </PublicLayout>
              }
            />

            {/* -----------------------------------------------------
                DASHBOARD
                ----------------------------------------------------- */}

            <Route
              path="/dashboard"
              element={
                <PublicLayout>
                  <RedirectUser />
                </PublicLayout>
              }
            />

            {/* -----------------------------------------------------
                PUBLIC LEGAL PAGES
                ----------------------------------------------------- */}

            <Route
              path="/information-security"
              element={
                <PublicLayout>
                  <InformationSecurityPolicy />
                </PublicLayout>
              }
            />

            <Route
              path="/cookie-policy"
              element={
                <PublicLayout>
                  <CookiePolicy />
                </PublicLayout>
              }
            />

            <Route
              path="/privacy-policy"
              element={
                <PublicLayout>
                  <PrivacyPolicy />
                </PublicLayout>
              }
            />

            <Route
              path="/terms-and-conditions"
              element={
                <PublicLayout>
                  <TermsAndConditions />
                </PublicLayout>
              }
            />
          </Routes>
        </Suspense>
      </SignedOut>

      {/* =========================================================
          SIGNED IN
          ========================================================= */}

      <SignedIn>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* -----------------------------------------------------
                ROOT

                Signed in + no destination
                    -> Destination Selector

                Signed in + destination
                    -> Home
                ----------------------------------------------------- */}

            <Route
              path="/"
              element={
                hasDestination ? (
                  <Navigate to="/home" replace />
                ) : (
                  <DestinationSelector />
                )
              }
            />

            {/* -----------------------------------------------------
                HOME

                Destination is mandatory.
                ----------------------------------------------------- */}

            <Route
              path="/home"
              element={
                hasDestination ? (
                  <ProtectedLayout>
                    <Home />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* -----------------------------------------------------
                ABOUT US

                Destination is mandatory for signed-in users.
                ----------------------------------------------------- */}

            <Route
              path="/about-us"
              element={
                hasDestination ? (
                  <ProtectedLayout showCountryNavbar={false}>
                    <AboutUs />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* -----------------------------------------------------
                APPLY NOW

                Destination is mandatory.
                ----------------------------------------------------- */}

            <Route
              path="/apply-now"
              element={
                hasDestination ? (
                  <ProtectedLayout>
                    <ApplyNow />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* -----------------------------------------------------
                JOBS
                ----------------------------------------------------- */}

            <Route
              path="/jobs"
              element={
                hasDestination ? (
                  <ProtectedLayout>
                    <Jobs />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/jobs/:page/:id"
              element={
                hasDestination ? (
                  <ProtectedLayout showCountryNavbar={false}>
                    <JobDetail />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* -----------------------------------------------------
                DASHBOARD
                ----------------------------------------------------- */}

            <Route
              path="/dashboard"
              element={
                hasDestination ? (
                  <ProtectedLayout>
                    <Dashboard />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* -----------------------------------------------------
                LEGAL PAGES

                Still accessible after destination selection.
                ----------------------------------------------------- */}

            <Route
              path="/information-security"
              element={
                hasDestination ? (
                  <ProtectedLayout showCountryNavbar={false}>
                    <InformationSecurityPolicy />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/cookie-policy"
              element={
                hasDestination ? (
                  <ProtectedLayout showCountryNavbar={false}>
                    <CookiePolicy />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/privacy-policy"
              element={
                hasDestination ? (
                  <ProtectedLayout showCountryNavbar={false}>
                    <PrivacyPolicy />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/terms-and-conditions"
              element={
                hasDestination ? (
                  <ProtectedLayout showCountryNavbar={false}>
                    <TermsAndConditions />
                  </ProtectedLayout>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* -----------------------------------------------------
                FALLBACK

                Any unknown signed-in route goes to the correct
                entry point.
                ----------------------------------------------------- */}

            <Route
              path="*"
              element={
                hasDestination ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </Suspense>
      </SignedIn>

      {/* =========================================================
          COOKIES POPUP
          ========================================================= */}

      {cookiesPop && (
        <div className="fixed bottom-[1rem] left-[1rem] sm:bottom-[2rem] sm:left-[2rem] max-w-[19rem] sm:max-w-sm px-4 py-3 shadow-md transition-all duration-500 ease-in-out rounded-[10px] bg-[#dbe9ff] z-20">
          <div className="flex flex-col px-0 ltr:lg:pl-9 rtl:lg:pr-9">
            <div className="w-full flex justify-between items-center">
              <p className="text-md md:text-lg font-semibold m-0 font-Poppins text-myBlue">
                Cookies & Privacy
              </p>
            </div>

            <div className="flex flex-col gap-2 items-stretch ltr:lg:pr-10 rtl:lg:pl-10">
              <div className="flex-1">
                <p className="my-1 text-xs md:text-sm font-Poppins">
                  We use cookies to improve your experience and ensure your
                  privacy. We don’t serve personalized ads or third
                  parties,Sensitive info is always secure.
                </p>

                <div className="flex text-center text-[12px] mt-1 gap-2">
                  <span className="ltr:mr-4 ltr:last:mr-0 rtl:ml-4 rtl:last:ml-0">
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                      className="no-underline cursor-pointer shrink-0 text-blue-700"
                    >
                      Privacy policy
                    </a>
                  </span>

                  <span className="ltr:mr-4 ltr:last:mr-0 rtl:ml-4 rtl:last:ml-0">
                    <a
                      href="/cookie-policy"
                      className="no-underline cursor-pointer shrink-0 text-blue-700"
                    >
                      Cookie policy
                    </a>
                  </span>
                </div>
              </div>

              <div className="flex flex-col justify-around mt-4 lg:mt-0 ltr:lg:pl-14 rtl:lg:pr-14">
                <div className="flex-1 gap-2 items-center flex my-0">
                  <button
                    onClick={handleDecline}
                    className="flex-1 lg:flex-none ltr:mr-2 rtl:ml-2 flex justify-center items-center text-center cursor-pointer px-2 md:px-4 py-2 border border-transparent text-xs leading-4 font-black"
                  >
                    <span className="flex">
                      {decline && (
                        <Spinner
                          className="mr-2 h-4 w-4 text-myBlue"
                          color="white"
                        />
                      )}
                      Decline
                    </span>
                  </button>

                  <button
                    onClick={handleAccept}
                    className="flex-1 bg-myBlue rounded-[10px] text-white lg:flex-none flex justify-center items-center text-center cursor-pointer px-2 md:px-4 py-2 border border-transparent text-xs leading-4 font-black"
                  >
                    <span className="flex">
                      {loading && (
                        <Spinner
                          className="mr-2 h-4 w-4 text-myBlue"
                          color="white"
                        />
                      )}
                      Accept
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default App;
